import * as THREE from 'three';
import Door from '../models/door.js';

class DoorController {
  constructor(sceneManager, wallController) {
    this.scene = sceneManager.getScene();
    this.wallController = wallController;
    this.doors = [];
    
    // Stato posizionamento
    this.placementMode = false;
    this.selectedWall = null;
    this.previewDoor = null;
    this.previewMesh = null;

    // Tipo e dimensioni della porta in corso di posizionamento
    // (impostati da enablePlacementMode in base all'item del catalogo)
    this.currentDoorType   = 'swing'; // 'swing' | 'sliding' | 'double'
    this.currentDoorWidth  = 0.9;
    this.currentDoorHeight = 2.1;

    // Cache dell'ultima posizione calcolata dal raycast durante la preview:
    // viene riusata da placeDoor per garantire che il piazzamento coincida
    // esattamente con la preview (ground-plane e wall-raycast possono divergere)
    this._lastPreviewPositionOnWall = null;
    
    // Distanza minima tra aperture (40 cm)
    this.minimumSpacing = 0.4;
    
    // Raycaster per rilevamento pareti
    this.raycaster = new THREE.Raycaster();

    // Callback opzionale: chiamata quando porte vengono aggiunte/rimosse
    this.onOpeningsChange = null;

    // Etichetta misure live (distanze sinistra/destra durante il posizionamento)
    this.measurementLabel = null;
    this.setupMeasurementLabel();
  }

  // ETICHETTA MISURE LIVE
  setupMeasurementLabel() {
    this.measurementLabel = document.createElement('div');
    this.measurementLabel.id = 'door-measurement-label';
    Object.assign(this.measurementLabel.style, {
      position:      'fixed',
      background:    'rgba(10, 10, 30, 0.88)',
      color:         '#ffffff',
      padding:       '5px 14px',
      borderRadius:  '20px',
      fontSize:      '14px',
      fontWeight:    'bold',
      fontFamily:    'system-ui, sans-serif',
      letterSpacing: '0.03em',
      pointerEvents: 'none',
      display:       'none',
      zIndex:        '2000',
      whiteSpace:    'nowrap',
      border:        '1.5px solid rgba(0,204,0,0.75)',
      boxShadow:     '0 2px 10px rgba(0,0,0,0.45)',
      transform:     'translate(-50%, -300%)',
    });
    document.body.appendChild(this.measurementLabel);
  }

  /**
   * Calcola e mostra le distanze tra il bordo della porta in preview e
   * le estremità della parete
   */
  updateMeasurementLabel(wall, positionOnWall, openingWidth, screenX, screenY, isValid, flipped = false) {
    if (!this.measurementLabel) return;

    const wallLength   = wall.getLength();
    const halfW        = openingWidth / 2 / wallLength;
    const openingStart = positionOnWall - halfW;
    const openingEnd   = positionOnWall + halfW;

    // Distanza dall'inizio e dalla fine della parete
    let leftDist  = Math.max(0, openingStart * wallLength);
    let rightDist = Math.max(0, (1 - openingEnd) * wallLength);

    // Se la camera guarda la parete dal lato opposto, sinistra e destra
    // risultano visivamente scambiate → inverto i valori
    if (flipped) [leftDist, rightDist] = [rightDist, leftDist];

    const fmt = d => d < 1 ? `${(d * 100).toFixed(0)} cm` : `${d.toFixed(2)} m`;

    this.measurementLabel.textContent  = `← ${fmt(leftDist)}  ·  ${fmt(rightDist)} →`;
    this.measurementLabel.style.left   = `${screenX}px`;
    this.measurementLabel.style.top    = `${screenY}px`;
    this.measurementLabel.style.display     = 'block';
    this.measurementLabel.style.borderColor = isValid
      ? 'rgba(0,204,0,0.75)'
      : 'rgba(255,60,60,0.85)';
  }

  hideMeasurementLabel() {
    if (this.measurementLabel) this.measurementLabel.style.display = 'none';
  }
  
  enablePlacementMode(catalogItem) {
    // Legge tipo e dimensioni dall'item del catalogo
    if (catalogItem) {
      this.currentDoorType   = catalogItem.doorType   || 'swing';
      this.currentDoorWidth  = catalogItem.dimensions?.width  ?? 0.9;
      this.currentDoorHeight = catalogItem.dimensions?.height ?? 2.1;
    }

    this.placementMode = true;
    this.createPreview();
    console.log(`Modalità posizionamento porte: ATTIVA (tipo=${this.currentDoorType}, ${this.currentDoorWidth}×${this.currentDoorHeight})`);
  }
  
  disablePlacementMode() {
    this.placementMode = false;
    this.removePreview();
    this.selectedWall = null;
    this.hideMeasurementLabel();
    console.log('Modalità posizionamento porte: DISATTIVATA');
  }
  
  togglePlacementMode() {
    if (this.placementMode) {
      this.disablePlacementMode();
    } else {
      this.enablePlacementMode();
    }
  }
  
  createPreview() {
    this.removePreview();

    const w = this.currentDoorWidth;
    const h = this.currentDoorHeight;
    const d = 0.22;

    const ft = 0.08; // spessore montante — uguale a _buildSwingDoor / _buildDoubleDoor

    // Per battente e doppia: telaio con montanti (ft su ogni lato)
    // Per scorrevole: telaio con montanti + spazio di scorrimento (w aggiuntivi)
    const previewW = this.currentDoorType === 'sliding'
      ? w + ft * 2 + w
      : w + ft * 2;

    const previewMaterial = new THREE.MeshBasicMaterial({
      color: 0x00cc00,
      transparent: true,
      opacity: 0.5,
      wireframe: true,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });

    const geometry = new THREE.BoxGeometry(previewW, h, d);
    this.previewMesh = new THREE.Mesh(geometry, previewMaterial);
    this.previewMesh.visible = false;
    this.scene.add(this.previewMesh);
  }
  
  removePreview() {
    if (this.previewMesh) {
      this.scene.remove(this.previewMesh);
      this.previewMesh.geometry.dispose();
      this.previewMesh.material.dispose();
      this.previewMesh = null;
    }
  }
  
  updatePreview(point, mouseEvent, camera) {
    if (!this.placementMode || !this.previewMesh) return;
    
    const w = this.currentDoorWidth;

    // Trova la parete più vicina usando il raycasting diretto
    const wallResult = this.findNearestWallWithRaycast(point, mouseEvent, camera);
    
    if (wallResult.wall) {
      this.selectedWall = wallResult.wall;
      const wall = wallResult.wall;
      
      // Usa la posizione calcolata dal raycast se disponibile, altrimenti fallback
      const positionOnWall = wallResult.positionOnWall !== null 
        ? wallResult.positionOnWall 
        : this.getPositionOnWall(wall, point);

      // Salva la posizione per placeDoor
      this._lastPreviewPositionOnWall = positionOnWall;
      
      // Aggiorna preview
      const worldPos = new THREE.Vector3().lerpVectors(
        wall.start, 
        wall.end, 
        positionOnWall
      );

      // Per la scorrevole il box è asimmetrico: il telaio è centrato su positionOnWall
      // ma lo spazio di scorrimento si estende di w verso destra → sposta il centro di w/2
      if (this.currentDoorType === 'sliding') {
        const angle = wall.getAngle();
        worldPos.x += Math.cos(angle) * (w / 2);
        worldPos.z -= Math.sin(angle) * (w / 2);
      }

      this.previewMesh.position.copy(worldPos);
      this.previewMesh.position.y = this.currentDoorHeight / 2;
      this.previewMesh.rotation.y = wall.getAngle();
      
      // Cambia colore in base alla validità
      const isValid = this.validatePosition(wall, positionOnWall, w, this.currentDoorType);
      this.previewMesh.material.color.setHex(isValid ? 0x00cc00 : 0xff0000);
      this.previewMesh.visible = true;

      // Mostra le distanze live
      if (mouseEvent && mouseEvent.clientX !== undefined) {
        // Determina se la camera guarda la parete dal lato opposto alla normale:
        // la normale canonica punta a 90° sinistra rispetto alla direzione start→end
        // Se il vettore camera→parete ha componente positiva lungo la normale,
        // la camera è sul lato "frontale"; altrimenti è sul lato opposto e
        // sinistra/destra vanno scambiate
        let flipped = false;
        if (camera) {
          const angle   = wall.getAngle();
          // Normale sinistra rispetto alla direzione start→end
          const nx = -Math.sin(angle);
          const nz =  Math.cos(angle);
          const wallCenter = wall.getCenter();
          const toCam = new THREE.Vector3(
            camera.position.x - wallCenter.x,
            0,
            camera.position.z - wallCenter.z
          );
          // dot < 0 → camera è sul lato opposto alla normale → label invertita
          flipped = (toCam.x * nx + toCam.z * nz) < 0;
        }
        this.updateMeasurementLabel(wall, positionOnWall, w,
          mouseEvent.clientX, mouseEvent.clientY, isValid, flipped);
      }
    } else {
      this.previewMesh.visible = false;
      this.selectedWall = null;
      this._lastPreviewPositionOnWall = null;
      this.hideMeasurementLabel();
    }
  }
  
  placeDoor(point) {
    if (!this.placementMode || !this.selectedWall) return null;
    
    const wall = this.selectedWall;
    const w = this.currentDoorWidth;
    const h = this.currentDoorHeight;

    // Usa la posizione già calcolata dal raycast durante l'updatePreview;
    // se per qualche motivo non fosse disponibile, ricalcola dal punto ground
    const positionOnWall = this._lastPreviewPositionOnWall !== null
      ? this._lastPreviewPositionOnWall
      : this.getPositionOnWall(wall, point);
    
    // Validazione
    if (!this.validatePosition(wall, positionOnWall, w)) {
      console.warn('Posizione non valida per la porta');
      return null;
    }
    
    // Crea la porta con tipo e dimensioni correnti
    const door = new Door(wall, positionOnWall, w, h, this.currentDoorType);
    this.addDoor(door);
    console.log(`Porta ${this.currentDoorType} posizionata (${w}×${h}):`, door);
    return door;
  }
  
  addDoor(door) {
    // Aggiunge porta alla parete
    if (!door.wall.doors) {
      door.wall.doors = [];
    }
    door.wall.doors.push(door);
    
    // Crea mesh e aggiunge alla scena
    const mesh = door.createMesh();
    this.scene.add(mesh);
    
    // Aggiunge alla lista
    this.doors.push(door);
    
    // Ricostruisce la parete con l'apertura
    door.wall.rebuildWallSegments();
    
    this.onOpeningsChange?.();
    return door;
  }
  
  removeDoor(doorId) {
    const index = this.doors.findIndex(d => d.id === doorId);
    if (index === -1) return false;
    
    const door = this.doors[index];
    const wall = door.wall;
    
    // Rimuove dalla parete
    if (wall.doors) {
      const wallIndex = wall.doors.findIndex(d => d.id === doorId);
      if (wallIndex !== -1) {
        wall.doors.splice(wallIndex, 1);
      }
    }
    
    // Rimuove dalla scena
    this.scene.remove(door.mesh);
    door.dispose();
    
    // Rimuove dalla lista
    this.doors.splice(index, 1);
    
    // Ricostruisce la parete
    wall.rebuildWallSegments();
    
    this.onOpeningsChange?.();
    return true;
  }
  
  findNearestWallWithRaycast(point, mouseEvent, camera) {
    const walls = this.wallController.getAllWalls();
    if (walls.length === 0) return { wall: null, positionOnWall: null };
    
    // Se ho mouseEvent e camera, usa il raycast diretto sulle pareti
    if (mouseEvent && camera) {
      const wallMeshes = [];
      const wallMap = new Map();
      
      walls.forEach(wall => {
        if (wall.mesh) {
          wall.mesh.traverse(child => {
            if (child.isMesh) {
              wallMeshes.push(child);
              wallMap.set(child, wall);
            }
          });
        }
      });
      
      if (wallMeshes.length > 0) {
        const rect = mouseEvent.target?.getBoundingClientRect?.() ??
                     this.raycaster.domElement?.getBoundingClientRect?.() ??
                     { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
        const mouse = new THREE.Vector2(
          ((mouseEvent.clientX - rect.left) / rect.width) * 2 - 1,
          -((mouseEvent.clientY - rect.top) / rect.height) * 2 + 1
        );
        
        this.raycaster.setFromCamera(mouse, camera);
        const intersects = this.raycaster.intersectObjects(wallMeshes, false);
        
        if (intersects.length > 0) {
          const intersectedMesh = intersects[0].object;
          const wall = wallMap.get(intersectedMesh);
          
          if (wall) {
            const intersectionPoint = intersects[0].point;
            const positionOnWall = this.getPositionOnWall(wall, intersectionPoint);
            
            return { wall, positionOnWall };
          }
        }
      }
    }
    
    // Fallback: usa il metodo classico basato sulla distanza dal ground plane
    let nearestWall = null;
    let minDistance = Infinity;
    const maxDistance = 1.5;
    
    walls.forEach(wall => {
      const distance = this.distanceToWall(point, wall);
      if (distance < minDistance && distance < maxDistance) {
        minDistance = distance;
        nearestWall = wall;
      }
    });
    
    return { wall: nearestWall, positionOnWall: null };
  }
  
  findNearestWall(point) {
    const walls = this.wallController.getAllWalls();
    if (walls.length === 0) return null;
    
    let nearestWall = null;
    let minDistance = Infinity;
    const maxDistance = 1.5;
    
    walls.forEach(wall => {
      const distance = this.distanceToWall(point, wall);
      if (distance < minDistance && distance < maxDistance) {
        minDistance = distance;
        nearestWall = wall;
      }
    });
    
    return nearestWall;
  }
  
  distanceToWall(point, wall) {
    // Distanza punto-segmento
    const start = wall.start.clone();
    const end = wall.end.clone();
    const p = new THREE.Vector3(point.x, 0, point.z);
    
    const lineVec = new THREE.Vector3().subVectors(end, start);
    const lineLen = lineVec.length();
    lineVec.normalize();
    
    const pointVec = new THREE.Vector3().subVectors(p, start);
    const t = pointVec.dot(lineVec);
    
    let closestPoint;
    if (t <= 0) {
      closestPoint = start;
    } else if (t >= lineLen) {
      closestPoint = end;
    } else {
      closestPoint = start.clone().add(lineVec.multiplyScalar(t));
    }
    
    return p.distanceTo(closestPoint);
  }
  
  getPositionOnWall(wall, point) {
    // Calcola posizione normalizzata (0-1) sulla parete
    const start = wall.start.clone();
    const end = wall.end.clone();
    const p = new THREE.Vector3(point.x, 0, point.z);
    
    const lineVec = new THREE.Vector3().subVectors(end, start);
    const lineLen = lineVec.length();
    lineVec.normalize();
    
    const pointVec = new THREE.Vector3().subVectors(p, start);
    const t = pointVec.dot(lineVec) / lineLen;
    
    return Math.max(0, Math.min(1, t));
  }
  
  validatePosition(wall, positionOnWall, doorWidth, doorType = 'swing') {
    // Per la scorrevole il footprint è asimmetrico: lato sinistro = width/2, lato destro = width/2 + (width-0.15)
    const halfLeft  = doorWidth / 2;
    const halfRight = doorType === 'sliding' ? doorWidth / 2 + doorWidth : doorWidth / 2;
    const wallLen   = wall.getLength();

    const margin = halfLeft / wallLen;
    if (positionOnWall < margin || positionOnWall + halfRight / wallLen > 1) {
      return false;
    }

    const newDoorStart = positionOnWall - halfLeft  / wallLen;
    const newDoorEnd   = positionOnWall + halfRight / wallLen;
    const spacingNorm  = this.minimumSpacing / wallLen;

    // Verifica sovrapposizione con altre porte (usa occupancy box per la scorrevole)
    if (wall.doors) {
      for (const existingDoor of wall.doors) {
        const doorBox = existingDoor.getOccupancyBox
          ? existingDoor.getOccupancyBox()
          : existingDoor.getBoundingBox();
        if (!(newDoorEnd + spacingNorm < doorBox.start ||
              newDoorStart - spacingNorm > doorBox.end)) {
          return false;
        }
      }
    }

    // Verifica sovrapposizione con finestre
    if (wall.windows) {
      for (const window of wall.windows) {
        const windowBox = window.getBoundingBox();
        if (!(newDoorEnd + spacingNorm < windowBox.start ||
              newDoorStart - spacingNorm > windowBox.end)) {
          return false;
        }
      }
    }

    return true;
  }
  
  getAllDoors() {
    return this.doors;
  }
  
  getDoorById(id) {
    return this.doors.find(d => d.id === id);
  }
  
  clearDoors() {
    this.doors.forEach(door => {
      this.scene.remove(door.mesh);
      door.dispose();
    });
    this.doors = [];
  }
  
  dispose() {
    this.doors.forEach(door => {
      this.scene.remove(door.mesh);
      door.dispose();
    });
    this.doors = [];
    this.removePreview();
    if (this.measurementLabel && this.measurementLabel.parentNode) {
      this.measurementLabel.parentNode.removeChild(this.measurementLabel);
      this.measurementLabel = null;
    }
  }
}

export default DoorController;